import {
  Badge,
  createStyles,
  Group,
  Tabs,
  TabsProps,
  Text,
  Tooltip,
} from '@mantine/core';
import React, { ReactNode } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import { useRequestPlanUpgrade } from '@portals/api/partners';
import { UpgradeTierBadgeWithText } from '@portals/framework';
import { ReactComponent as GlobalIcon } from '@portals/icons/bold/global.svg';
import { ReactComponent as ShopIcon } from '@portals/icons/bulk/shop.svg';
import { ReactComponent as SmsIcon } from '@portals/icons/bulk/sms.svg';
import { VerticalScrollBar } from '@portals/scrollbar';

import { CustomerPortalForm } from './customer-portal/CustomerPortalForm';
import { EmailsForm } from './emails/EmailsForm';
import { StoreForm } from './store/StoreForm';
import { usePricingPlan } from '../../../hooks/partner-config';
import { BrandingType } from '../branding.types';

interface TabItem {
  title: string;
  value: BrandingType;
  isBeta?: boolean;
  isAvailable?: boolean;
  icon: ReactNode;
  component: ReactNode;
}

export function BrandingForm() {
  const pricingPlan = usePricingPlan();
  const isAllowedToEdit =
    pricingPlan === 'enterprise' || pricingPlan === 'premium';

  const { classes } = useStyles();
  const params = useParams<{ type: BrandingType }>();
  const history = useHistory();
  const requestPlanUpgrade = useRequestPlanUpgrade();

  function badgeRenderer(tab: TabItem) {
    if (tab.isAvailable) {
      return (
        <UpgradeTierBadgeWithText
          message={
            <Text size="xs">
              This feature is not included in your current tier, please upgrade
              to activate.
            </Text>
          }
          onUpgrade={() =>
            requestPlanUpgrade.mutate(
              `Partners > Branding > ${tab.title} > Upgrade to premium plan`
            )
          }
        >
          Upgrade
        </UpgradeTierBadgeWithText>
      );
    }

    if (tab.isBeta) {
      return (
        <Badge
          h={24}
          c="gray.9"
          fw={400}
          fz="xs"
          variant="gradient"
          gradient={{ from: '#E9F3F7', to: '#DEDBFD', deg: 108 }}
        >
          BETA
        </Badge>
      );
    }

    return null;
  }

  const TABS_LIST: Array<TabItem> = [
    {
      title: 'Portal branding',
      value: BrandingType.CustomerPortal,
      icon: <GlobalIcon />,
      component: <CustomerPortalForm />,
    },
    {
      title: 'Store branding',
      value: BrandingType.Store,
      isBeta: true,
      isAvailable: !isAllowedToEdit,
      icon: <ShopIcon />,
      component: <StoreForm disabled={!isAllowedToEdit} />,
    },
    {
      title: 'Emails branding',
      value: BrandingType.Email,
      isBeta: true,
      isAvailable: !isAllowedToEdit,
      icon: <SmsIcon />,
      component: <EmailsForm disabled={!isAllowedToEdit} />,
    },
  ];

  return (
    <Tabs
      keepMounted={false}
      variant="outline"
      radius={0}
      styles={tabsStyles}
      value={params.type}
      defaultValue={BrandingType.CustomerPortal}
      onTabChange={(tab) => history.push(`/branding/${tab}`)}
    >
      <Tabs.List grow>
        {TABS_LIST.map((tab) => (
          <Tooltip withinPortal label={tab.title} key={tab.value}>
            <Tabs.Tab value={tab.value}>{tab.icon}</Tabs.Tab>
          </Tooltip>
        ))}
      </Tabs.List>

      {TABS_LIST.map((tab) => (
        <Tabs.Panel key={tab.value} value={tab.value}>
          <Group position="apart" className={classes.tabTitle}>
            <Text size="xl" weight={500}>
              {tab.title}
            </Text>

            {badgeRenderer(tab)}
          </Group>

          <VerticalScrollBar>{tab.component}</VerticalScrollBar>
        </Tabs.Panel>
      ))}
    </Tabs>
  );
}

const useStyles = createStyles((theme) => ({
  tabTitle: {
    paddingInline: theme.spacing.xl,
    paddingBlock: theme.spacing.lg,
    borderBottom: `2px solid ${theme.colors.gray[2]}`,
  },
}));

const tabsStyles: TabsProps['styles'] = (theme) => ({
  root: {
    display: 'grid',
    gridTemplateRows: 'max-content 1fr',
    borderRight: `2px solid ${theme.colors.gray[2]}`,
  },
  tabsList: {
    height: 56,
    borderBottomWidth: 2,
    borderColor: theme.colors.gray[2],
  },
  tab: {
    borderTop: 0,
    borderColor: theme.colors.gray[2],
    backgroundColor: theme.colors.gray[0],

    '&[data-active]': {
      borderColor: theme.colors.gray[2],
      backgroundColor: theme.white,

      '&:before': {
        height: 2,
        bottom: -2,
      },
    },

    '&:first-of-type': {
      borderLeft: 'none',
    },

    '&:last-of-type': {
      borderRight: 'none',
    },
  },
  panel: {
    display: 'grid',
    gridTemplateRows: 'max-content 1fr',
  },
});
